<script setup lang="ts">
import type { Schedule, ScheduleEvent, Team } from '~/types'

defineOptions({
  name: 'ScheduleEventsTeam',
})

interface Props {
  team: Team.Model
  scheduleId: string
}

const props = defineProps<Props>()
const scheduleId = computed<number>(() => Number(props.scheduleId))
const { id: teamId } = useTeam(props.team)
const { errorObject: scheduleEventsErrorObject } = useShowApiError('Schedule Events Not Found')

const scheduleEventsParams = computed<ScheduleEvent.SearchRequest>(() => ({
  include: [
    'awayTeam.logo',
    'homeTeam.logo',
    'scheduleEventBroadcasts.logo',
    'scheduleEventLinks',
    'scheduleEventScoring',
    'scheduleEventSponsors.logo',
    'scheduleWeek',
    'venue',
  ],
  per_page: 50,
  filter: {
    'scheduleWeek.schedule_id': scheduleId.value ?? undefined,
    'team_id': teamId.value ?? undefined,
  },
}))

const scheduleParams = computed<Schedule.SearchRequest>(() => ({
  include: [
    'season', 'scheduleWeeks',
  ],
}))

const { fetchData } = useLoadSchedule(scheduleId, scheduleParams)
const { data: scheduleData } = await useAsyncData(`schedule-${scheduleId.value}`, () => fetchData(), { watch: [scheduleId] })

const { fetchAllData, isLoading } = useLoadScheduleEvents(scheduleEventsParams)
const { data: scheduleEventsData, error: scheduleEventsError } = await useAsyncData('all-schedule-events', () => fetchAllData(), { watch: [scheduleEventsParams] })

if (scheduleEventsError.value) {
  throw scheduleEventsErrorObject
}

const { hasScheduleWeeks, scheduleWeeks } = useSchedule(scheduleData)

const scheduleEvents = computed<ScheduleEvent.Model[]>(() => scheduleEventsData.value || [])
const hasScheduleEvents = computed<boolean>(() => scheduleEvents.value.length > 0)
</script>

<template>
  <section class="team-schedule-list">
    <UILoadingCard
      v-if="isLoading"
    />
    <div v-else-if="hasScheduleWeeks && hasScheduleEvents">
      <template
        v-for="(week, index) in scheduleWeeks"
        :key="`schedule-events-week-${week.id}`"
      >
        <ScheduleEventsWeekName
          :week="week"
          class="team-schedule-list__heading"
        />
        <ScheduleEventsAllByWeek
          v-if="hasScheduleEvents"
          :schedule-events="scheduleEvents"
          :week-id="week.id"
          class="team-schedule-list__list"
        >
          <template #default="{ eventsByWeek }">
            <ScheduleEventsTeamItem
              v-for="scheduleEvent in eventsByWeek"
              :key="`schedule-events-${scheduleEvent.id}`"
              :schedule-event
              :current-team="team"
              class="team-schedule-list__item"
            />
          </template>
        </ScheduleEventsAllByWeek>
        <!-- TODO: Replace with some slot -->
        <AdPlacement
          v-if="index === 4"
          :team
          class="team-schedule-list__ad"
        />
      </template>
    </div>
    <div
      v-else
      class="team-schedule-list__empty"
    >
      <UIDynamicHeading
        level="4"
        as="h4"
      >
        Events not found
      </UIDynamicHeading>
    </div>
  </section>
</template>

<style scoped lang="scss">
.team-schedule-list {
  @apply mb-[40px];
  @apply md:mb-[60px];
  @apply xl:mb-[100px];

  &__heading {
    @apply mb-[15px];
    @apply xl:mb-[20px];

    :deep(.schedule-events-week__title) {
      @apply text-[18px] text-primary/60 font-medium leading-[1];
    }
  }

  &__list {
    :deep(.schedule-events-by-week__list) {
      @apply m-0 gap-0;
    }
  }

  &__empty {
    @apply text-center;
  }
}
</style>
